import React, { createContext, useState, useContext, useMemo } from 'react';

interface CellIndex {
  rowIndex: number;
  columnIndex: number;
  isDismissed?: boolean;
}

export type filters = {
  actuals: boolean;
  money: boolean;
  percentage: boolean;
  smoothing: boolean;
  budget: boolean;
  variance: boolean;
  transactions: boolean;
  sparkLine: boolean;
  collapsed: boolean;
  anomalies: boolean;
};

const NTContext = createContext<{
  sparkLine?: 'bar' | 'line' | null;
  setSparkLine: (sparkLine: 'bar' | 'line' | null) => void;

  budgetAvailable?: boolean;
  setBudgetAvailable: (budgetAvailable: boolean) => void;

  columns: number;
  setColumns: (columns: number | null) => void;

  // table filters
  filters: filters;
  setFilters: (filters: filters) => void;

  reportId?: string;
  setReportId: (reportId: string) => void;

  dismissedAudit: CellIndex[];
  setDismissedAudit: (dismissedAudit: CellIndex[]) => void;
}>({
  sparkLine: null,
  setSparkLine: () => {},
  budgetAvailable: false,
  setBudgetAvailable: () => {},
  filters: {
    actuals: false,
    money: true,
    percentage: false,
    smoothing: false,
    budget: false,
    variance: false,
    transactions: false,
    sparkLine: false,
    collapsed: false,
    anomalies: false,
  },
  setFilters: () => {},
  columns: 0,
  setColumns: () => {},
  reportId: undefined,
  setReportId: () => {},
  dismissedAudit: [],
  setDismissedAudit: () => {},
});

export const NTProvider: React.FC<{
  children: React.ReactNode;
  initialFilters?: filters;
  initialBudgetAvailable?: boolean;
}> = ({ children, initialFilters, initialBudgetAvailable = false }) => {
  const [sparkLine, setSparkLine] = useState<'bar' | 'line' | null>('bar');
  const [budgetAvailable, setBudgetAvailable] = useState<boolean>(initialBudgetAvailable);
  const [columns, setColumns] = useState<number>(0);
  const [filters, setFilters] = useState({
    actuals: initialFilters?.actuals ?? false,
    money: initialFilters?.money ?? true,
    percentage: initialFilters?.percentage ?? false,
    smoothing: initialFilters?.smoothing ?? false,
    budget: initialFilters?.budget ?? false,
    variance: initialFilters?.variance ?? false,
    transactions: initialFilters?.transactions ?? false,
    sparkLine: initialFilters?.sparkLine ?? false,
    collapsed: initialFilters?.collapsed ?? false,
    anomalies: initialFilters?.anomalies ?? false,
  });
  const [reportId, setReportId] = useState<string | undefined>(undefined);
  const [dismissedAudit, setDismissedAudit] = useState<CellIndex[]>([]);

  const contextValue = useMemo(
    () => ({
      sparkLine,
      setSparkLine,
      filters,
      setFilters,
      budgetAvailable,
      setBudgetAvailable,
      columns,
      setColumns: (value: number | null) => setColumns(value ?? 0),
      reportId,
      setReportId,
      dismissedAudit,
      setDismissedAudit: (value: CellIndex[]) => setDismissedAudit(value),
    }),
    [sparkLine, filters, budgetAvailable, columns, reportId, dismissedAudit]
  );
  return <NTContext.Provider value={contextValue}>{children}</NTContext.Provider>;
};
export const useNTContext = () => useContext(NTContext);
